import { HubConnection, HubConnectionState } from '@microsoft/signalr';
import AppContext from 'App/AppContext';
import { CopilotAction } from 'models/copilot/copilotAction';
import { CopilotPanelContentBase, CopilotPanelContext } from 'models/copilot/copilotBridge';
import { CopilotConversation, CopilotConversationContentBlock } from 'models/copilot/copilotConversation';
import { CopilotDocumentSelection } from 'models/copilot/copilotDocumentSelection';
import { CopilotSelectedDocument } from 'models/copilot/copilotGeneric';
import { CopilotPreviewContent } from 'models/copilot/copilotPreviewContent';
import { DocumentEditMode } from 'models/userSetting';
import {
  Dispatch,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from 'react';
import { IHubConnection, stopHubConnection } from 'services/SignalR/GeneralHub';
import { CopilotApprovalOperation } from '../Panel/Dialogs/CopilotApprovalDetailsModal';
import { PendingDeleteApproval } from '../Panel/Dialogs/CopilotPanelDialogs';
import { getCopilotPanelContextDefinitionMap } from '../Panel/Runtime/copilotPanelContextDefinitions';
import { copilotStreamReducer, createInitialStreamState } from '../Panel/Runtime/copilotStreamReducer';
import { useCopilotChatController } from '../Panel/Runtime/useCopilotChatController';
import { useCopilotRuntime } from '../Panel/Runtime/useCopilotRuntime';
import { useCopilotStreaming } from '../Panel/Runtime/useCopilotStreaming';

// Stable empty actions reference: an agent session run from a modal never drives a process.
const NO_ACTIONS: CopilotAction[] = [];

export interface CopilotAgentSessionSeed {
  sessionId: number;
  conversation: CopilotConversation;
}

interface UseCopilotAgentSessionProps {
  // Context key the backend uses to scope the session (e.g. the panel's CopilotPanelContext name).
  contextKey: string;
  // The content the session is bound to, when the backend has to know which entity the conversation
  // is about (the generic definition maps it to the entity ref of every completion). The evidence pane
  // beside a task hands over a CopilotGenericContent carrying the task; the document editor has none.
  content?: CopilotPanelContentBase;
  // When provided, the session continues an existing conversation/session (e.g. handed off from the
  // side panel). When omitted, a new session is created on the first question.
  initialSession?: CopilotAgentSessionSeed;
  // The document block this session is editing; exposed to the backend as the selected document so it
  // registers the docx/page tools and knows which content block to operate on.
  selectedDocumentBlock?: CopilotConversationContentBlock;
  // How the agent should apply its edits ('direct' | 'tracked'); passed to the backend on each run so it
  // steers the agent. Sourced from the user's documentEditModeAI setting. Defaults to 'direct' when omitted.
  aiEditMode?: DocumentEditMode;
  // Reads the current document text selection at send time (or null when nothing is selected). When it
  // returns a selection, it is attached to the request so the agent can ground "change this" on the highlight.
  getSelection?: () => CopilotDocumentSelection | null;
  // One-shot reference to the tenant-authored instruction that runs on the first submitted instruction in this
  // session. The backend resolves the instruction from the database and runs its text; only the reference is sent.
  instructionExternalId?: string;
}

// Self-contained Copilot agent session: owns the conversation, streaming state, hub connection and the
// chat controller, composed from the same hooks the side panel uses. Reusable outside the panel (e.g.
// inside the document edit modal) so the agent can drive docx/page edits over the SignalR tool bridge.
export const useCopilotAgentSession = (props: UseCopilotAgentSessionProps) => {
  const { contextKey, initialSession, selectedDocumentBlock, aiEditMode } = props;
  // Kept in a ref so createSelectedDocumentRequest reads the latest selection getter without re-creating on
  // every render (the modal supplies a fresh inline getter each render).
  const getSelectionRef = useRef(props.getSelection);
  getSelectionRef.current = props.getSelection;
  const instructionExternalIdRef = useRef(props.instructionExternalId);
  const instructionExternalIdConsumedRef = useRef(false);
  const lastSeenInstructionExternalIdRef = useRef(props.instructionExternalId);
  const appContext = useContext(AppContext);
  const contextDefinitions = useMemo(() => getCopilotPanelContextDefinitionMap(), []);
  const genericDefinition = useMemo(() => contextDefinitions.get(CopilotPanelContext.Generic), [contextDefinitions]);

  const [conversation, setConversation] = useState<CopilotConversation | undefined>(initialSession?.conversation);
  const [copilotSessionId, setCopilotSessionId] = useState<number | undefined>(initialSession?.sessionId);
  const [streamState, dispatchStream] = useReducer(copilotStreamReducer, undefined, createInitialStreamState);
  const [hubConnection, setHubConnection] = useState<HubConnection | undefined>(undefined);
  const [isStreaming, setIsStreaming] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [previewContent, setPreviewContent] = useState<CopilotPreviewContent>(CopilotPreviewContent.Document);
  const [pendingDeleteApproval, setPendingDeleteApproval] = useState<PendingDeleteApproval | null>(null);
  const [approvalDetailsOperations, setApprovalDetailsOperations] = useState<CopilotApprovalOperation[] | null>(null);
  const abortControllerRef = useRef<AbortController | undefined>(undefined);

  const setStreamStatus = useCallback<Dispatch<SetStateAction<string | undefined>>>(
    (value) => {
      const status = typeof value === 'function' ? value(streamState.status) : value;
      dispatchStream({ type: 'status', status });
    },
    [streamState.status],
  );

  const { getCopilotHubConnection } = useCopilotStreaming({
    appContext,
    conversation,
    setConversation,
    setConversationCreator: () => undefined,
    setConversationDate: () => undefined,
    setPreviewContent,
    setStreamStatus,
    dispatchStream,
  });

  // One persistent hub connection for the whole editing session. The live editor subscribes to it for the
  // agent->editor command bridge and every run reuses it, so editor commands are never dropped between runs and
  // the subscription never races a fresh per-run connection. It is stopped on unmount (see effect below).
  const persistentHubRef = useRef<IHubConnection | null>(null);
  const persistentHubPromiseRef = useRef<Promise<IHubConnection | null> | null>(null);

  const getPersistentHubConnection = useCallback(async (): Promise<IHubConnection | null> => {
    const existing = persistentHubRef.current;
    if (existing?.connection && existing.connection.state === HubConnectionState.Connected) {
      return existing;
    }

    // Coalesce concurrent callers (e.g. a run starting while autosave opens) onto one connection attempt.
    if (persistentHubPromiseRef.current) return persistentHubPromiseRef.current;

    const creator = (async () => {
      try {
        const opened = await getCopilotHubConnection();
        persistentHubRef.current = opened;

        return opened;
      } finally {
        persistentHubPromiseRef.current = null;
      }
    })();
    persistentHubPromiseRef.current = creator;

    return creator;
  }, [getCopilotHubConnection]);

  const createSelectedDocumentRequest = useCallback((): CopilotSelectedDocument | undefined => {
    if (!selectedDocumentBlock?.id) return undefined;

    const selectedDocument = new CopilotSelectedDocument();
    selectedDocument.contentBlockId = selectedDocumentBlock.id;
    selectedDocument.version = selectedDocumentBlock.version || 1;
    selectedDocument.title = selectedDocumentBlock.title;
    selectedDocument.isDocumentEditorSession = true;
    selectedDocument.documentEditMode = aiEditMode ?? 'direct';

    // Attach the current text selection so the agent can ground "change this" on the highlight. Read at send
    // time from the tracked selection (kept even after the editor blurs, e.g. clicking into the chat input).
    const selection = getSelectionRef.current?.();
    if (selection?.text) {
      selectedDocument.selectionText = selection.text;
      selectedDocument.selectionStartBlockId = selection.startBlockId;
      selectedDocument.selectionStartOffset = selection.startOffset;
      selectedDocument.selectionEndBlockId = selection.endBlockId;
      selectedDocument.selectionEndOffset = selection.endOffset;
    }

    return selectedDocument;
  }, [selectedDocumentBlock, aiEditMode]);

  const getInstructionExternalId = useCallback((): string | undefined => {
    if (instructionExternalIdConsumedRef.current) return undefined;

    const instructionExternalId = instructionExternalIdRef.current;
    instructionExternalIdConsumedRef.current = true;

    return instructionExternalId || undefined;
  }, []);

  useEffect(() => {
    const next = props.instructionExternalId;
    // Re-arm the one-shot only when a genuinely new, non-empty reference arrives. Ignoring empty
    // transitions keeps an already-consumed reference from being resent if the parent briefly clears and
    // restores the same value, so a follow-up chat turn never reruns the instruction.
    if (!next || next === lastSeenInstructionExternalIdRef.current) return;

    lastSeenInstructionExternalIdRef.current = next;
    instructionExternalIdRef.current = next;
    instructionExternalIdConsumedRef.current = false;
  }, [props.instructionExternalId]);

  const getRuntime = useCopilotRuntime({
    appContext,
    content: props.content,
    conversation,
    actions: NO_ACTIONS,
    activeAction: undefined,
    copilotSessionId,
    copilotContextKey: contextKey,
    previewContent,
    setCopilotSessionId,
    setPreviewContent,
    setConversation,
    setIsLoading,
    setStreamStatus,
    createSelectedDocumentRequest,
    getInstructionExternalId,
    getUsageState: () => undefined,
    //this session has no actions and runs no processes, so there is nothing to start or end
    onStartProcess: async () => undefined,
    onEndProcess: async () => undefined,
    // getAnswer is defined by the chat controller below; the closure only reads it when invoked.
    submitConversation: async (nextConversation, runtimeActions, nextTargetFolder) =>
      getAnswer(nextConversation, runtimeActions, nextTargetFolder),
  });

  const {
    getAnswer,
    onNewQuestion,
    onEditLastQuestion,
    onEntitySelection,
    onAdaptiveInputsChanged,
    onAdaptiveSubmit,
    onApprovalDetails,
    submitTaskApproval,
  } = useCopilotChatController({
    appContext,
    conversation,
    actions: NO_ACTIONS,
    copilotSessionId,
    editPreviewItems: false,
    hasRunningProcess: false,
    activeDefinition: genericDefinition,
    genericDefinition,
    contextDefinitions,
    keepConnectionAlive: true,
    abortControllerRef,
    getRuntime,
    getCopilotHubConnection: getPersistentHubConnection,
    setConversation,
    setCopilotSessionId,
    setHubConnection,
    setIsLoading,
    setIsStreaming,
    setStreamStatus,
    setPendingDeleteApproval,
    setApprovalDetailsOperations,
    dispatchStream,
    clearPreviewState: () => undefined,
    cleanUpProcess: () => undefined,
  });

  // Open the persistent connection as soon as the session has a document to edit and publish it as the hub
  // connection, so the live editor attaches its agent-command handler before the first run rather than racing
  // the connection a run would otherwise create.
  useEffect(() => {
    if (!selectedDocumentBlock?.id) return undefined;

    let cancelled = false;
    void getPersistentHubConnection().then((opened) => {
      if (!cancelled && opened?.connection) setHubConnection(opened.connection);
    });

    return () => {
      cancelled = true;
    };
  }, [getPersistentHubConnection, selectedDocumentBlock?.id]);

  // Stop the session's persistent connection on unmount.
  useEffect(
    () => () => {
      void stopHubConnection(persistentHubRef.current?.connection);
      persistentHubRef.current = null;
    },
    [],
  );

  const onStop = useCallback(() => {
    abortControllerRef.current?.abort();
  }, []);

  return {
    conversation,
    setConversation,
    copilotSessionId,
    streamState,
    dispatchStream,
    isStreaming,
    isLoading,
    hubConnection,
    // Exposed so callers (e.g. the document edit modal) reuse the session's single persistent connection for
    // autosave instead of opening a second one.
    getCopilotHubConnection: getPersistentHubConnection,
    pendingDeleteApproval,
    setPendingDeleteApproval,
    approvalDetailsOperations,
    setApprovalDetailsOperations,
    onNewQuestion,
    onEditLastQuestion,
    onEntitySelection,
    onAdaptiveInputsChanged,
    onAdaptiveSubmit,
    onApprovalDetails,
    submitTaskApproval,
    onStop,
  };
};
